import { setMinutes, setHours, addDays, startOfDay, differenceInCalendarDays } from 'date-fns'
import FileSaver from 'file-saver'
import { Capacitor } from '@capacitor/core'

import { store } from 'store/reducers'
import { Format, Parse } from 'services/Dates'
import { getEventCss, getEventHtml } from 'services/schedule'
import handleMobileSaveFiles from 'services/utilFunctions/handleMobileAppSaveFiles'
import { t } from 'translations'

export const BASE_DATE = new Date('2010-02-01T12:00:00')

export const somewhatRandomID = () => `${Math.floor(100000000000000000 * Math.random() + 100000000000000000)}`

export const getMultiDayIndex = ({ start, multiDayIndex = 0 }) => (start.length === 5 ? multiDayIndex : differenceInCalendarDays(Parse(start), BASE_DATE))

export const getSeparators = days => {
  const separators = []
  for (let day = 1; day < days; day++) separators.push({ color: '#111', width: 2, opacity: 75, location: Format(startOfDay(addDays(BASE_DATE, day))) })
  return separators
}

export const parseFromTemplate = (event, { templateInfo = {} } = {}) => {
  const {
    settings: { schedulerEventsHighContrast }
  } = store.getState()

  const getDateTimeFromTime = (time, offset) => {
    if (time.length !== 5) return time
    const [hours, minutes] = time.split(':')
    return addDays(setMinutes(setHours(BASE_DATE, hours), minutes), offset)
  }

  const offset = event.multiDayIndex || 0
  let [start, end] = [event.start, event.end].map(time => getDateTimeFromTime(time, offset))

  // If end is before start, add one day to end
  if (start >= end) end = addDays(end, 1)

  // Format the start and end
  event = { ...event, start: Format(start), end: Format(end) }

  const [html, cssClass] = [
    getEventHtml({ ...event, ...templateInfo }, { inTemplate: true, multiDayTemplate: true }),
    getEventCss({ ...event, ...templateInfo }, schedulerEventsHighContrast)
  ]
  return {
    ...event,
    id: event.eventId || event.id || somewhatRandomID(),
    resource: event.positionId,
    html,
    cssClass
  }
}

export const parseToTemplate = ({
  id,
  eventId,
  employeeId,
  start,
  startTimezone,
  end,
  endTimezone,
  resource,
  positionId,
  multiDayIndex = 0, // Avoids putting shifts on a random date
  breaks = [],
  comment,
  isExtraShift,
  isOvertime,
  isIrreplaceable,
  needsReplacement,
  noEmployeeAvailable,
  hasSpecificRequirements,
  coveredOtherUnit,
  projects,
  tags = [],
  wagePremiums = [],
  requiredAttributes = [],
  billingDetails = { billingId: '' }
}) => {
  if (start.value) start = start.value
  if (end.value) end = end.value
  return {
    id: id || eventId,
    employeeId,
    eventId,
    multiDayIndex,
    start: start.length === 5 ? start : Format(Parse(start), { outFormat: 'serverTime' }),
    startTimezone,
    end: end.length === 5 ? end : Format(Parse(end), { outFormat: 'serverTime' }),
    endTimezone,
    positionId: resource || positionId,
    breaks: breaks.map(brk => {
      if (typeof brk.duration !== 'string') return brk
      const [hours, minutes] = brk.duration.split(':')
      if (typeof hours !== 'undefined' && typeof minutes !== 'undefined') {
        return { ...brk, duration: parseInt(hours) * 60 + parseInt(minutes) }
      } else return brk
    }),
    comment,
    projects,
    tags,
    wagePremiums,
    isExtraShift: !!isExtraShift,
    isOvertime: !!isOvertime,
    isIrreplaceable: !!isIrreplaceable,
    needsReplacement: !!needsReplacement,
    noEmployeeAvailable: !!noEmployeeAvailable,
    hasSpecificRequirements: !!hasSpecificRequirements,
    coveredOtherUnit: !!coveredOtherUnit,
    billingDetails,
    requiredAttributes
  }
}

export const importTemplateHeaders = {
  template: {
    mandatory: ['name'],
    optional: [
      'projectCode',
      'defaultShiftTags',
      'departments',
      'requiredPositions',
      'comment',
      'defaultShiftComment',
      'allShiftsIrreplaceable',
      'combineShiftAndTemplateTags'
    ]
  },
  events: {
    mandatory: ['employeeNumber', 'positionCode', 'startTime', 'endTime'],
    optional: [
      'dayInTemplate',
      'projectCode',
      'tags',
      'comment',
      'isExtraShift',
      'isOvertime',
      'nonBillable',
      'isIrreplaceable',
      'needsReplacement',
      'noEmployeeAvailable',
      'hasSpecificRequirements',
      'hasSpecificRequirements',
      'coveredOtherUnit'
    ]
  }
}

// This import template is different from the others since it has two header fields
export const getImportTemplateCSV = () => {
  const csv = `${[...importTemplateHeaders.template.mandatory, ...importTemplateHeaders.template.optional]
    .map(h => `"${h}"`)
    .join(',')}\n\n-- DO NOT DELETE OR ADD ROWS BEFORE THIS ONE--\n${[...importTemplateHeaders.events.mandatory, ...importTemplateHeaders.events.optional]
    .map(h => `"${h}"`)
    .join(',')}`
  const type = 'text/csv'

  // Saves the csv file using the FileSaver module
  return Capacitor.isNativePlatform()
    ? handleMobileSaveFiles(csv, t('manager.scheduleTemplates.import.'), type)
    : FileSaver.saveAs(new Blob([csv], { type }), t('manager.scheduleTemplates.import.FILENAME'))
}
